import React, { useEffect, useState } from 'react';

interface TypewriterProps {
  words: string[];
  colors?: string[];
}

const Typewriter: React.FC<TypewriterProps> = ({ words, colors = [] }) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [reduced, setReduced] = useState(false);

  const longest = words.reduce((longestWord, word) =>
    word.length > longestWord.length ? word : longestWord
  );

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (reduced) return;

    const word = words[index];
    const finishedTyping = text === word;
    const finishedDeleting = text === '';
    let delay = deleting ? 42 : 88;

    if (!deleting && finishedTyping) delay = 1600;
    if (deleting && finishedDeleting) delay = 320;

    const timer = window.setTimeout(() => {
      if (!deleting) {
        if (finishedTyping) {
          setDeleting(true);
        } else {
          setText(word.slice(0, text.length + 1));
        }
        return;
      }

      if (finishedDeleting) {
        setDeleting(false);
        setIndex((current) => (current + 1) % words.length);
        return;
      }

      setText(word.slice(0, text.length - 1));
    }, delay);

    return () => window.clearTimeout(timer);
  }, [deleting, index, reduced, text, words]);

  const shown = reduced ? words[0] : text;
  const color = colors[reduced ? 0 : index];

  return (
    <span className="inline-grid align-baseline text-center" style={color ? { color } : undefined}>
      <span className="invisible col-start-1 row-start-1 whitespace-nowrap" aria-hidden="true">
        {longest}
      </span>
      <span className="col-start-1 row-start-1 whitespace-nowrap">
        {shown}
        {!reduced && <span className="typing-caret" aria-hidden="true" />}
      </span>
    </span>
  );
};

export default Typewriter;
